import { DefaultLoader, LoaderContainer } from 'components/Loader';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  setCustomTags,
  setOrder,
  setPage,
  setSearch,
  setTags,
  setTake,
} from 'store/actions/admin/mentorLists';
import { StoreState } from 'store/reducers';
import useSWR from 'swr';
import GetOrderValue from 'types/GetOrderValue';
import Tags from 'types/Tags';
import { Action } from 'types/typeguards/reducerActions';
import User from 'types/User';
import getTagsQueryParams from 'utils/tagQueryParam';
import Head from './index-components/Head';
import MentorsFilters from './index-components/MentorsFilters';
import MentorsList from './index-components/MentorsList';

export const tagColumnNames = [
  'referee',
  'status',
  'jobSearch',
  'careerPlan',
  'studies',
  'entrepreneurship',
  'expertiseFields',
  'formations',
  'diploma',
  'consideration',
  'hobby',
  'wantedMentorships',
] as const;
export type TagColumnNames = typeof tagColumnNames[number];

export const customTagColumnNames = ['eventStartDate'] as const;
export type CustomTagColumnNames = typeof customTagColumnNames[number];
export type CustomTags = {
  eventStartDate: string;
};

type RetrievedData = {
  mentors: User[] | undefined;
  count: number | undefined;
  pages: number | undefined;
  filteredCount: number | undefined;
};

export const sortableColumns = [
  'firstName',
  'lastName',
  'priority',
  'organism',
  'createdAt',
  'status',
] as const;
export type OrderValue = GetOrderValue<typeof sortableColumns[number]>;

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  tags: Tags<TagColumnNames>;
  setTags: (tag: Tags<TagColumnNames>) => void;
  customTags: CustomTags;
  setCustomTags: (customTags: CustomTags) => void;
  take: number;
  setTake: (take: number) => void;
  order: OrderValue;
  setOrder: (order: OrderValue) => void;
};

type ContextType = RetrievedData & {
  loading: boolean;
} & Props;
export const MentorsContext = React.createContext({} as ContextType);
const { Provider } = MentorsContext;

const Mentors: FC<Props> = ({
  children,
  page,
  setPage,
  search,
  setSearch,
  tags,
  setTags,
  customTags,
  setCustomTags,
  take,
  setTake,
  order,
  setOrder,
}) => {
  const { data, error } = useSWR<RetrievedData>(
    `/user/mentor/?page=${page}&take=${take}\
		${search ? `&search=${search}` : ''}\
		${
      tags.status[0]
        ? tags.status.map((tag) => `&mentorTags=${tag}`).join('')
        : ''
    }\
		${getTagsQueryParams<TagColumnNames>(tags, [
      'referee',
      'jobSearch',
      'careerPlan',
      'studies',
      'entrepreneurship',
      'expertiseFields',
      'formations',
      'diploma',
      'consideration',
      'hobby',
      'wantedMentorships',
    ])}\
&order=${order}\
${
  customTags.eventStartDate
    ? `&eventStartDateTags=${customTags.eventStartDate}`
    : ''
}
		`,
  );

  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    mentors: undefined,
    count: undefined,
    pages: undefined,
    filteredCount: undefined,
  });
  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);

  // Reset page to 0 if the stored page is out of range
  useEffect(() => {
    if (data?.pages && data?.pages <= page) {
      setPage(0);
    }
  }, [data, page, setPage]);

  const value = useMemo(
    () => ({
      ...retrievedData,
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      customTags,
      setCustomTags,
      loading: !data,
      take,
      setTake,
      order,
      setOrder,
    }),
    [
      page,
      search,
      tags,
      setPage,
      setSearch,
      setTags,
      customTags,
      setCustomTags,
      retrievedData,
      data,
      take,
      setTake,
      order,
      setOrder,
    ],
  );

  return retrievedData.mentors || error ? (
    <Provider value={value}>{children}</Provider>
  ) : (
    <LoaderContainer>
      <DefaultLoader size={1.5} />
    </LoaderContainer>
  );
};

const Usage: FC<Props> = (props) => {
  return (
    <Mentors {...props}>
      <Head />
      <MentorsFilters />
      <MentorsList />
    </Mentors>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.admin.mentorLists.main.page,
    search: state.admin.mentorLists.main.search,
    tags: state.admin.mentorLists.main.tags,
    customTags: state.admin.mentorLists.main.customTags,
    take: state.admin.mentorLists.main.take,
    order: state.admin.mentorLists.main.order,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('main', page)),
    setSearch: (search: string) => dispatch(setSearch('main', search)),
    setTags: (tags: Tags<TagColumnNames>) =>
      dispatch(setTags<TagColumnNames>('main', tags)),
    setCustomTags: (customTags: CustomTags) =>
      dispatch(setCustomTags('main', customTags)),
    setTake: (take: number) => dispatch(setTake('main', take)),
    setOrder: (order: OrderValue) => dispatch(setOrder('main', order)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Usage);
